import React from "react";
import "./index.css"
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { doc, onSnapshot } from "firebase/firestore";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate
} from "react-router-dom";
import { CardMedia, ThemeProvider, CssBaseline, GlobalStyles } from "@mui/material";
import Login from "./components/login";
import Home from "./components/home";
import Profile from "./components/profile";
import Notifications from "./components/notifications";
import Publish from "./components/publish";
import ClaimVerification from "./components/ClaimVerification";
import Edit from "./components/edit";
import ChatBox from "./components/chatbox";
import Chats from "./components/chats";
import Trips from "./components/trips";
import Terms from "./components/terms";
import Ver from "./components/ver";
import Properties from "./components/myProperties";
import Policy from "./components/policy";
import Support from "./components/support";
import Header3 from "./components/Header3";
import { endSession } from "./session";
import { db, isProfileComplete, isRegistering } from "./firebase";
import logo from "./imgs/wandr-logo.svg";
import theme from "./theme";

const Loading = () => (
  <div className="loading-container">
    <div className="loading-panel">
      <CardMedia
        className="loading-img"
        alt="Cargando..."
        component="img"
        image={logo}
      />
    </div>
  </div>
);

// Componente de ruta protegida
const ProtectedRoute = ({ children, user }) => {
  if (!user) {
    // Redirige al login si no hay usuario autenticado
    return <Navigate to="/login" replace />;
  }
  return children;
};

const App = () => {
  const [user, setUser] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  // undefined while loading; false sends the user to /complete-profile (e.g. first Google sign-in).
  const [profileComplete, setProfileComplete] = React.useState(undefined);

  React.useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(
      auth,
      (firebaseUser) => {
        setUser(firebaseUser);
        setLoading(false);
      },
      (error) => {
        console.error("Error al verificar la autenticación:", error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  React.useEffect(() => {
    setProfileComplete(undefined);
    if (!user) return undefined;
    return onSnapshot(
      doc(db, "users", user.uid),
      (snapshot) => {
        // An email sign-up writes its profile right after creating the account: wait for it.
        if (!snapshot.exists() && isRegistering()) return;
        setProfileComplete(isProfileComplete(snapshot.data()));
      },
      (error) => {
        // Don't lock the user out over a read error; the pages handle a missing profile.
        console.error("Error al leer el perfil:", error);
        setProfileComplete(true);
      }
    );
  }, [user]);

  const handleLogout = async () => {
    try {
      await endSession();
      setUser(null);
    } catch (error) {
      console.error("Error al cerrar sesión:", error);
    }
  };

  // Rutas públicas que no requieren autenticación
  // eslint-disable-next-line no-unused-vars
  const publicRoutes = ['/login', '/register', '/terms', '/policy'];

  if (loading || (user && profileComplete === undefined)) {
    return <Loading />;
  }

  // Signed in without a complete profile: only the completion form (and the legal pages) are reachable.
  const mustCompleteProfile = Boolean(user) && profileComplete === false;

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <GlobalStyles
        styles={{
          "*": { boxSizing: "border-box" },
          ":root": {
            colorScheme: "dark",
          },
        }}
      />
      <div className="app-shell">
        <Router>
          {mustCompleteProfile ? (
          <Routes>
            <Route path="/complete-profile" element={<Login initialView="complete" />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/policy" element={<Policy />} />
            <Route path="*" element={<Navigate to="/complete-profile" replace />} />
          </Routes>
          ) : (
          <Routes>
            {/* Rutas públicas */}
            <Route path="/complete-profile" element={<Navigate to={user ? "/home" : "/login"} replace />} />
            <Route path="/login" element={user ? <Navigate to="/home" replace /> : <Login />} />
            <Route path="/register" element={user ? <Navigate to="/home" replace /> : <Login initialView="register" />} />
            <Route path="/forgot-password" element={<Login initialView="forgot" />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/policy" element={<Policy />} />

            {/* Ruta raíz */}
            <Route
              path="/"
              element={user ? <Navigate to="/home" replace /> : <Navigate to="/login" replace />}
            />

            {/* Rutas protegidas */}
            <Route
              path="/verificar/:placeId"
              element={
                <ProtectedRoute user={user}>
                  <ClaimVerification />
                </ProtectedRoute>
              }
            />
            <Route
              path="/home"
              element={
                <ProtectedRoute user={user}>
                  <Home handleLogout={handleLogout} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute user={user}>
                  <Profile />
                </ProtectedRoute>
              }
            />
            <Route
              path="/notifications"
              element={
                <ProtectedRoute user={user}>
                  <Notifications />
                </ProtectedRoute>
              }
            />
            <Route
              path="/ver/:placeId"
              element={
                <ProtectedRoute user={user}>
                  <Ver />
                </ProtectedRoute>
              }
            />
            <Route
              path="/chatbox/:placeId/:placeUid/:authUserId"
              element={
                <ProtectedRoute user={user}>
                  <ChatBox />
                </ProtectedRoute>
              }
            />
            <Route
              path="/publish"
              element={
                <ProtectedRoute user={user}>
                  <Publish />
                </ProtectedRoute>
              }
            />
            <Route
              path="/trips"
              element={
                <ProtectedRoute user={user}>
                  <Trips />
                </ProtectedRoute>
              }
            />
            <Route
              path="/chats"
              element={
                <ProtectedRoute user={user}>
                  <Chats />
                </ProtectedRoute>
              }
            />
            <Route
              path="/edit"
              element={
                <ProtectedRoute user={user}>
                  <Edit />
                </ProtectedRoute>
              }
            />
            <Route
              path="/myProperties"
              element={
                <ProtectedRoute user={user}>
                  <Properties />
                </ProtectedRoute>
              }
            />
            <Route
              path="/support"
              element={
                <ProtectedRoute user={user}>
                  <Support />
                </ProtectedRoute>
              }
            />
            {/* Header3 también protegido si es necesario */}
            <Route
              path="/Header3"
              element={
                <ProtectedRoute user={user}>
                  <Header3 />
                </ProtectedRoute>
              }
            />
          </Routes>
          )}
        </Router>
      </div>
    </ThemeProvider>
  );
};

export default App;